/**
 * posSaveRouter — chooses online vs offline POS save based on connectivity.
 *
 * Drop-in replacement for direct posFastWriteHelper usage. POSScreen imports
 * from here so it doesn't care which path runs.
 *
 * - When online: runs the existing fast-write path AND mirrors the result
 *   into local Dexie (so the offline cache stays current with the latest sales).
 * - When offline: runs the local-only save path (Dexie + outbox).
 *
 * Same applies to preloadPOSCaches (fall back to Dexie when offline) and
 * allocatePOSDocNumber (fall back to local range allocator).
 */

import {
  preloadPOSCaches as cloudPreloadPOSCaches,
  allocatePOSDocNumber as cloudAllocatePOSDocNumber,
  savePOSTransactionFast as cloudSavePOSTransactionFast,
  buildPOSSaleBundle,
  preparedToOutboxBundle
} from './posFastWriteHelper';
import { savePOSTransactionOffline } from './posOfflineSave';
import { assertPosLocationUnlocked } from './stockAuditHelper';
import { mirrorOnlinePOSSaleToLocal, readCollection, savePOSSaleLocally } from './posLocalCache';
import { getNextDocNumber } from './posDocCounterRange';
import { isOnline } from './connectivity';
import { SYSTEM_ACCOUNTS } from '../config/tranTypes';

// ─── preloadPOSCaches: route by connectivity ────────────────────────────

/**
 * Build the { systemAccounts, defaultLocation, partyAccountByCustomerId }
 * cache that savePOSTransactionFast needs. Cloud fetch when online; falls
 * back to whatever was synced into Dexie when offline.
 */
export const preloadPOSCaches = async (companyId) => {
  if (isOnline()) {
    try {
      return await cloudPreloadPOSCaches(companyId);
    } catch (err) {
      console.warn('[posSaveRouter] online preload failed, falling back to local cache:', err);
      // Fall through to local
    }
  }
  return await buildOfflineCachesFromDexie(companyId);
};

const buildOfflineCachesFromDexie = async (companyId) => {
  const accounts = await readCollection('accounts', companyId);
  const systemAccounts = {};
  for (const code of Object.values(SYSTEM_ACCOUNTS)) {
    const acc = accounts.find(a => a.code === code);
    if (acc) systemAccounts[code] = { id: acc.id, code: acc.code, name: acc.name };
  }
  const locations = await readCollection('inventory_locations', companyId);
  const defaultLocation = locations.find(l => l.isDefault) || locations[0] || { id: null, locationName: 'Main' };
  const partyAccountByCustomerId = {};
  for (const acc of accounts) {
    if (acc.linkedEntityType === 'customer' && acc.linkedEntityId) {
      partyAccountByCustomerId[acc.linkedEntityId] = { id: acc.id, code: acc.code, name: acc.name };
    }
  }
  return { systemAccounts, defaultLocation, partyAccountByCustomerId };
};

// ─── allocatePOSDocNumber: route by connectivity ────────────────────────

/**
 * Allocate the next POS document number. When online uses the existing
 * cloud-counter runTransaction; when offline pulls from the pre-allocated
 * range stored locally.
 */
export const allocatePOSDocNumber = async (companyId, tranTypeCode, docDate, branchId = null, counter = null) => {
  if (isOnline()) {
    try {
      return await cloudAllocatePOSDocNumber(companyId, tranTypeCode, docDate, branchId, counter);
    } catch (err) {
      console.warn('[posSaveRouter] online doc# allocation failed, falling back to local range:', err);
      // Fall through to local
    }
  }
  return await getNextDocNumber({
    companyId, tranTypeCode, counter, isOnline: isOnline()
  });
};

// ─── savePOSTransactionFast: route by connectivity ──────────────────────

/**
 * Same signature as posFastWriteHelper.savePOSTransactionFast.
 * When online: cloud save + mirror to local Dexie (so cache stays current).
 * When offline: pure local save + outbox enqueue.
 */
export const savePOSTransactionFast = async (companyId, saleData, userId, caches) => {
  // Stock Audit freeze — one guard here covers BOTH the online and offline paths.
  // A POS sale never carries an explicit locationId, so resolve it the same way the
  // writers do (saleData → branch/default location cache). No-op when nothing is
  // being counted (a single cached read of a tiny doc).
  const effLocationId = saleData?.locationId || caches?.defaultLocation?.id || null;
  if (effLocationId) {
    await assertPosLocationUnlocked(companyId, effLocationId);
  }

  // Bank card discount sales / their returns write the card's usage and the
  // claim log alongside the sale, which the offline queue does not carry —
  // so they are online-only (the POS also blocks this before charging).
  if ((saleData?.bankDiscount || saleData?.bankDiscountReturn) && !isOnline()) {
    throw new Error('Bank card discount sales need internet — reconnect and try again.');
  }

  if (isOnline()) {
    const result = await cloudSavePOSTransactionFast(companyId, saleData, userId, caches);
    // Mirror to Dexie in the background — non-fatal if it fails
    if (result?._bundle) {
      mirrorOnlinePOSSaleToLocal(companyId, result._bundle)
        .catch(err => console.warn('[posSaveRouter] mirror to local failed:', err));
    }
    return result;
  }
  // Offline path
  return await savePOSTransactionOffline(companyId, saleData, userId, caches);
};

// ─── POS-01: build before printing, never lose a printed sale ────────────

/**
 * Build the complete sale (header + ledger + stock rows, with their ids) and run
 * every check that could make the save fail — pure work on the loaded caches,
 * no Firestore read. Throws a clear error; call it BEFORE printing.
 * opts.validateOnly: checks only (before a doc number is taken).
 */
export const preparePOSSale = (companyId, saleData, userId, caches, opts = {}) =>
  buildPOSSaleBundle(companyId, saleData, userId, caches, opts);

// Firestore codes that mean "try again later" (connection / server busy).
const TEMPORARY_CODES = new Set([
  'unavailable', 'deadline-exceeded', 'aborted', 'cancelled',
  'resource-exhausted', 'internal', 'unknown', 'unauthenticated'
]);

/** True when a failed save is worth retrying as-is (network / server busy). */
export const isTemporarySaveError = (err) => {
  const code = String(err?.code || '').replace(/^firestore\//, '').toLowerCase();
  if (TEMPORARY_CODES.has(code)) return true;
  const msg = String(err?.message || err || '').toLowerCase();
  return /network|offline|internet|failed to fetch|timed? ?out|connection/.test(msg);
};

/**
 * Save a sale that was built with preparePOSSale (and already printed).
 * If the normal save fails for ANY reason, the SAME bundle (same transaction
 * id) is put in the offline outbox; posSyncWorker uploads it later and skips a
 * sale whose id is already in the cloud — so it can never be saved twice.
 *
 * @returns {Promise<{status:'saved', result} | {status:'queued', temporary:boolean, error}>}
 *   Rejects only when the sale could not be kept anywhere (error.code 'pos/not-saved').
 */
export const savePreparedPOSSale = async (companyId, saleData, userId, caches, prepared) => {
  try {
    const result = await savePOSTransactionFast(companyId, { ...saleData, prepared }, userId, caches);
    return { status: 'saved', result };
  } catch (err) {
    try {
      await savePOSSaleLocally(companyId, preparedToOutboxBundle(prepared));
    } catch (queueErr) {
      console.error('[posSaveRouter] could not keep the sale in the outbox:', queueErr);
      const e = new Error(`${err?.message || err} (it could not be kept on this device either: ${queueErr?.message || queueErr})`);
      e.code = 'pos/not-saved';
      e.cause = err;
      throw e;
    }
    console.warn('[posSaveRouter] save failed — sale kept in the offline outbox:', err);
    return { status: 'queued', temporary: isTemporarySaveError(err), error: err };
  }
};

// Re-export the same default shape so any module importing default keeps working
export default {
  preloadPOSCaches,
  allocatePOSDocNumber,
  savePOSTransactionFast,
  preparePOSSale,
  savePreparedPOSSale
};
